
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import {
    Search,
    User,
    Phone,
    FileText,
    ChevronLeft,
    ChevronRight,
    Loader2,
    Calendar,
    X,
    Download,
    Building2,
    Mail
} from 'lucide-react';

const AuctionEnquiriesPage = () => {
    const [inquiries, setInquiries] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [pagination, setPagination] = useState({});

    const fetchInquiries = async (pageNum = 1, search = searchTerm, start = startDate, end = endDate) => {
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            let url = `/api/admin/auction-inquiries?page=${pageNum}&limit=10&search=${search}`;

            if (start) url += `&startDate=${start}`;
            if (end) url += `&endDate=${end}`;

            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setInquiries(response.data.data);
                setPagination(response.data.pagination);
                setPage(pageNum);
            }
        } catch (error) {
            console.error('Error fetching auction enquiries:', error);
            toast.error('Failed to load auction enquiries');
        } finally {
            setLoading(false);
        }
    };

    const handleDateSearch = () => {
        fetchInquiries(1, searchTerm, startDate, endDate);
    };

    const handleClearDates = () => {
        setStartDate('');
        setEndDate('');
        fetchInquiries(1, searchTerm, '', '');
    };

    const handleExportToExcel = async () => {
        try {
            const token = localStorage.getItem('token');
            let url = `/api/admin/auction-inquiries/export?search=${searchTerm}`;

            if (startDate) url += `&startDate=${startDate}`;
            if (endDate) url += `&endDate=${endDate}`;

            const response = await axios.get(url, {
                headers: { Authorization: `Bearer ${token}` },
                responseType: 'blob'
            });

            // Create download link
            const blob = new Blob([response.data], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = `auction_enquiries_${new Date().toISOString().split('T')[0]}.xlsx`;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(downloadUrl);

            toast.success('Excel file downloaded successfully!');
        } catch (error) {
            console.error('Error exporting to Excel:', error);
            toast.error('Failed to export data');
        }
    };

    // Check if download button should be shown
    const showDownloadButton = searchTerm || startDate || endDate;

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchInquiries(1, searchTerm);
        }, 500);

        return () => clearTimeout(delayDebounceFn);
    }, [searchTerm]);

    return (
        <div className="p-6 max-w-7xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex flex-col gap-4">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl font-bold text-slate-800">Auction Enquiries</h1>
                        <p className="text-slate-500 mt-1">Review enquiries submitted for E-Auction properties</p>
                    </div>

                    <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
                        {/* Global Search */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
                            <input
                                type="text"
                                placeholder="Search name, phone, email, property..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-full md:w-64"
                            />
                        </div>

                        {/* Download Button - Only visible when filters are active */}
                        {showDownloadButton && (
                            <button
                                onClick={handleExportToExcel}
                                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors font-medium flex items-center justify-center gap-2 whitespace-nowrap"
                            >
                                <Download size={18} />
                                Download Excel
                            </button>
                        )}
                    </div>
                </div>

                {/* Date Range Filter */}
                <div className="bg-white p-4 rounded-lg border border-slate-200 shadow-sm">
                    <div className="flex flex-col md:flex-row gap-3 items-start md:items-center">
                        <div className="flex items-center gap-2">
                            <Calendar className="text-slate-400" size={18} />
                            <span className="text-sm font-medium text-slate-600">Date Range:</span>
                        </div>

                        <div className="flex flex-wrap gap-3 items-center">
                            <div className="flex flex-col">
                                <label className="text-xs text-slate-500 mb-1">Start Date</label>
                                <input
                                    type="date"
                                    value={startDate}
                                    onChange={(e) => setStartDate(e.target.value)}
                                    className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                />
                            </div>

                            <div className="flex flex-col">
                                <label className="text-xs text-slate-500 mb-1">End Date</label>
                                <input
                                    type="date"
                                    value={endDate}
                                    onChange={(e) => setEndDate(e.target.value)}
                                    className="px-3 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                                />
                            </div>

                            <div className="flex gap-2 mt-5">
                                <button
                                    onClick={handleDateSearch}
                                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm flex items-center gap-2"
                                >
                                    <Search size={16} />
                                    Search
                                </button>

                                <button
                                    onClick={handleClearDates}
                                    className="px-4 py-2 bg-slate-100 text-slate-600 rounded-lg hover:bg-slate-200 transition-colors font-medium text-sm flex items-center gap-2"
                                >
                                    <X size={16} />
                                    Clear
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Table Card */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-200">
                                <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider w-16">S.No</th>
                                <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Customer</th>
                                <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Property Details</th>
                                <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Message</th>
                                <th className="px-6 py-4 text-xs font-semibold text-slate-500 uppercase tracking-wider">Date</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                            {loading ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-12 text-center text-slate-500">
                                        <div className="flex justify-center items-center gap-2">
                                            <Loader2 className="animate-spin h-5 w-5 text-blue-600" />
                                            <span>Loading inquiries...</span>
                                        </div>
                                    </td>
                                </tr>
                            ) : inquiries.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="px-6 py-8 text-center text-slate-500">
                                        No inquiries found.
                                    </td>
                                </tr>
                            ) : (
                                inquiries.map((inquiry, index) => (
                                    <tr key={inquiry.id} className="hover:bg-slate-50 transition-colors cursor-default group">
                                        <td className="px-6 py-4 text-sm text-slate-500">
                                            {(pagination.page - 1) * 10 + index + 1}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-center gap-3">
                                                <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 group-hover:bg-blue-50 group-hover:text-blue-600 transition-colors">
                                                    <User size={16} />
                                                </div>
                                                <div className="flex flex-col">
                                                    <span className="font-medium text-slate-700">{inquiry.name}</span>
                                                    <div className="flex flex-col gap-0.5 text-xs text-slate-500 mt-0.5">
                                                        <div className="flex items-center gap-1.5">
                                                            <Phone size={12} />
                                                            {inquiry.phone}
                                                        </div>
                                                        {inquiry.email && (
                                                            <div className="flex items-center gap-1.5">
                                                                <Mail size={12} />
                                                                {inquiry.email}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-start gap-2 text-sm text-slate-600">
                                                <Building2 size={14} className="text-slate-400 mt-0.5 flex-shrink-0" />
                                                <span className="font-medium text-slate-700">{inquiry.property_title}</span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="flex items-start gap-2 max-w-xs">
                                                <FileText size={14} className="text-slate-400 mt-0.5 flex-shrink-0" />
                                                <span className="text-sm text-slate-600 line-clamp-2" title={inquiry.message}>
                                                    {inquiry.message || '-'}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-slate-600">
                                            <div className="flex items-center gap-2">
                                                <Calendar size={14} className="text-slate-400" />
                                                {new Date(inquiry.created_at).toLocaleDateString('en-IN', {
                                                    day: '2-digit',
                                                    month: 'short',
                                                    year: 'numeric',
                                                    hour: '2-digit',
                                                    minute: '2-digit'
                                                })}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {!loading && pagination.totalPages > 1 && (
                    <div className="bg-slate-50 px-6 py-4 border-t border-slate-200 flex flex-col md:flex-row items-center justify-between gap-4">
                        <div className="text-sm text-slate-500">
                            Showing <span className="font-medium">{(pagination.page - 1) * 10 + 1}</span> to <span className="font-medium">{Math.min(pagination.page * 10, pagination.total)}</span> of <span className="font-medium">{pagination.total}</span> results
                        </div>

                        <div className="flex items-center gap-1">
                            <button
                                onClick={() => fetchInquiries(page - 1, searchTerm)}
                                disabled={page === 1}
                                className="p-2 rounded-md border border-slate-300 bg-white text-slate-500 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                <ChevronLeft size={16} />
                            </button>

                            {/* Simple pagination logic */}
                            {Array.from({ length: pagination.totalPages }, (_, i) => i + 1).map(p => (
                                <button
                                    key={p}
                                    onClick={() => fetchInquiries(p, searchTerm)}
                                    className={`w-8 h-8 flex items-center justify-center rounded-md border text-sm font-medium transition-colors ${p === page
                                        ? 'bg-blue-600 text-white border-blue-600'
                                        : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-50'
                                        }`}
                                >
                                    {p}
                                </button>
                            ))}

                            <button
                                onClick={() => fetchInquiries(page + 1, searchTerm)}
                                disabled={page === pagination.totalPages}
                                className="p-2 rounded-md border border-slate-300 bg-white text-slate-500 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                            >
                                <ChevronRight size={16} />
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AuctionEnquiriesPage;
